import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Row, Col, Form, Badge, Alert } from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaBuilding, FaCalendarAlt, FaExclamationTriangle,
  FaUserTie, FaTools, FaBox, FaCheckCircle, FaShoppingCart, FaArrowRight, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

// Função para formatar CPF com máscara
const formatCpf = (cpf) => {
  // Remove tudo que não for número
  const numericCpf = cpf.replace(/\D/g, '');
  
  // Aplica a máscara XXX.XXX.XXX-XX
  if (numericCpf.length <= 3) {
    return numericCpf;
  } else if (numericCpf.length <= 6) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3)}`;
  } else if (numericCpf.length <= 9) {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6)}`;
  } else {
    return `${numericCpf.slice(0, 3)}.${numericCpf.slice(3, 6)}.${numericCpf.slice(6, 9)}-${numericCpf.slice(9, 11)}`;
  }
};

// Função para remover máscara do CPF
const removeCpfMask = (cpf) => {
  return cpf.replace(/\D/g, '');
};

// Função para validar CPF
const validateCpf = (cpf) => {
  // Remove formatação
  const cleanCpf = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (cleanCpf.length !== 11) {
    return false;
  }
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCpf)) {
    return false;
  }
  
  // Valida primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
  }
  
  // CORREÇÃO: Primeiro calcular o resto, depois aplicar a regra
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(cleanCpf.charAt(9)) !== digit1) {
    return false;
  }
  
  // Valida segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
  }
  
  // CORREÇÃO: Primeiro calcular o resto, depois aplicar a regra
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return parseInt(cleanCpf.charAt(10)) === digit2;
};

// Componente dedicado para Prestadores de Serviço
const CredencialPrestador = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  // Estados
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editingPrestador, setEditingPrestador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('dados'); // NOVO: Controle de abas

  // Estados para validação de CPF
  const [cpfError, setCpfError] = useState('');
  const [cpfValid, setCpfValid] = useState(null);

  // Estados para controle do carrinho
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  
  // Dados
  const [prestadores, setPrestadores] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [expositorLogado, setExpositorLogado] = useState(null);
  const [expositores, setExpositores] = useState([]); // Apenas para admin
  const [produtos, setProdutos] = useState([]); // NOVO: Produtos vinculados a prestadores

  // Estado do formulário
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    empresa: '',
    cargo: '',
    evento_id: '',
    expositor_id: '' // Novo campo para o ID do expositor
  });
  
  // Filtros
  const [filters, setFilters] = useState({ search: '' });

  // Chave para armazenar o estado do formulário no localStorage
  const FORM_STATE_KEY = 'credencial_prestador_form_state';

  // Salvar e restaurar estado do formulário quando a visibilidade da página muda
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && showForm) {
        // Salvar estado atual do formulário no localStorage
        try {
          const stateToSave = {
            formData,
            editingPrestador,
            activeModalTab,
            showForm
          };
          
          localStorage.setItem(FORM_STATE_KEY, JSON.stringify(stateToSave));
          console.log('💾 Estado do formulário de credencial prestador salvo no localStorage');
        } catch (error) {
          console.error('Erro ao salvar estado do formulário:', error);
        }
      } else if (document.visibilityState === 'visible') {
        // Verificar se há estado salvo e restaurar
        try {
          const savedState = localStorage.getItem(FORM_STATE_KEY);
          if (savedState) {
            const parsedState = JSON.parse(savedState);
            
            // Restaurar o estado do formulário
            setFormData(parsedState.formData || {});
            setEditingPrestador(parsedState.editingPrestador);
            setActiveModalTab(parsedState.activeModalTab || 'dados');
            setShowForm(parsedState.showForm);
            
            console.log('🔄 Estado do formulário de credencial prestador restaurado do localStorage');
          }
        } catch (error) {
          console.error('Erro ao restaurar estado do formulário:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [formData, showForm, editingPrestador, activeModalTab]);

  // Limpar estado salvo quando o formulário é fechado
  useEffect(() => {
    if (!showForm) {
      localStorage.removeItem(FORM_STATE_KEY);
    }
  }, [showForm]);

  const isAdmin = () => hasPermission && (hasPermission('admin') || user?.role === 'admin');

  // Manipular mudança no campo CPF
  const handleCpfChange = (e) => {
    const { value } = e.target;
    
    // Remove tudo que não for número
    const cpfLimpo = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos
    if (cpfLimpo.length <= 11) {
      // Formata o CPF com máscara
      const cpfFormatado = formatCpf(cpfLimpo);
      
      setFormData({
        ...formData,
        cpf: cpfFormatado
      });

      // Validar CPF se estiver completo
      if (cpfLimpo.length === 11) {
        const isValid = validateCpf(cpfFormatado);
        setCpfValid(isValid);
        
        if (!isValid) {
          setCpfError('CPF inválido. Verifique os números digitados.');
        } else {
          setCpfError('');
        }
      } else {
        setCpfValid(null);
        setCpfError('');
      }
    }
  };

  // NOVA FUNÇÃO: Manipular clique nas abas
  const handleTabClick = (tab) => {
    setActiveModalTab(tab);
  };

  // NOVA FUNÇÃO: Buscar produtos vinculados a prestadores
  const fetchProdutos = async () => {
    try {
      console.log('Carregando produtos vinculados a prestadores...');
      
      const { data, error } = await supabase
        .from('caex_produtos')
        .select('id, nome, descricao, preco, imagem_url')
        .eq('ativo', true)
        .eq('vinculado_prestador', true)
        .order('nome');
      
      if (error) {
        console.error('Erro ao buscar produtos:', error);
        throw error;
      }
      
      console.log('Produtos carregados:', data);
      setProdutos(data || []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  // NOVA FUNÇÃO: Renderizar produtos vinculados a prestadores
  const renderProdutosDropdown = () => {
    if (produtos.length === 0) {
      return (
        <div style={{
          padding: '1rem',
          textAlign: 'center',
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px'
        }}>
          <FaBox style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }} />
          <div>Nenhum produto disponível para prestadores de serviço</div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
          Produtos vinculados a prestadores de serviço:
        </div>
        
        {produtos.map(produto => (
          <div
            key={produto.id}
            className="produto-card selected"
          >
            <div style={{ marginRight: '0.75rem', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#28a745',
                  cursor: 'not-allowed'
                }}
                readOnly
              />
            </div>
            
            <div className="produto-image">
              {produto.imagem_url ? (
                <img 
                  src={produto.imagem_url} 
                  alt={produto.nome}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                />
              ) : (
                <FaBox style={{ color: '#6c757d', fontSize: '1.2rem' }} />
              )}
            </div>
            
            <div className="produto-info">
              <div className="produto-nome">{produto.nome}</div>
              <div className="produto-preco">
                R$ {produto.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        ))}
        
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: '#d1ecf1',
          color: '#0c5460',
          border: '1px solid #bee5eb',
          borderRadius: '8px',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaCheckCircle />
          Todos os produtos estão automaticamente vinculados às credenciais de prestador
        </div>
      </div>
    );
  };

  // NOVA FUNÇÃO: Carregar carrinho do banco de dados
  const loadCartFromDatabase = async () => {
    if (!user?.id) {
      setCartItemsCount(0);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('caex_carrinho_pedido')
        .select('quantidade')
        .eq('usuario_id', user.id);

      if (error) throw error;

      const totalItems = data.reduce((total, item) => total + item.quantidade, 0);
      setCartItemsCount(totalItems);
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
      setCartItemsCount(0);
    }
  };

  // NOVA FUNÇÃO: Adicionar prestador ao carrinho no banco de dados
  const addPrestadorToCartDatabase = async (prestadorData) => {
    try {
      if (!user?.id) {
        throw new Error('Usuário não autenticado');
      }

      if (!prestadorData.nome || !prestadorData.cpf || !prestadorData.empresa || !prestadorData.cargo) {
        throw new Error('Dados do prestador incompletos');
      }

      // Buscar produto vinculado a prestadores
      const produtoReferencia = produtos.length > 0 ? produtos[0] : null;
      
      if (!produtoReferencia) {
        throw new Error('Nenhum produto disponível para prestadores');
      }

      // Preparar dados para o carrinho
      const cartData = {
        usuario_id: user.id,
        produto_id: produtoReferencia.id,
        quantidade: 1,
        preco_original: parseFloat(produtoReferencia.preco),
        preco_customizado: null,
        desconto: 0,
        tipo_desconto: 'value',
        dados_extras: {
          category: 'Prestador',
          prestadorData: {
            id: prestadorData.id,
            nome: prestadorData.nome,
            cpf: prestadorData.cpf,
            empresa: prestadorData.empresa,
            cargo: prestadorData.cargo,
            tipo_credencial: 'Prestador de Serviço',
            produto_id: produtoReferencia.id,
            produto_nome: produtoReferencia.nome,
            expositor_id: prestadorData.expositor_id,
            evento_id: prestadorData.evento_id
          }
        }
      };

      // Verificar se já existe item similar no carrinho
      const { data: existingItem } = await supabase
        .from('caex_carrinho_pedido')
        .select('*')
        .eq('usuario_id', user.id)
        .eq('produto_id', produtoReferencia.id)
        .contains('dados_extras', { category: 'Prestador' })
        .single();

      if (existingItem) {
        // Atualizar quantidade do item existente
        const { error } = await supabase
          .from('caex_carrinho_pedido')
          .update({
            quantidade: existingItem.quantidade + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingItem.id);

        if (error) throw error;
        console.log('Quantidade de prestador atualizada no carrinho');
      } else {
        // Inserir novo item
        const { error } = await supabase
          .from('caex_carrinho_pedido')
          .insert([cartData]);

        if (error) throw error;
        console.log('Novo prestador adicionado ao carrinho no banco');
      }

      // Atualizar contador do carrinho
      await loadCartFromDatabase();

      // Preparar dados para notificação
      const addedItem = {
        id: `prestador-${prestadorData.id}`,
        name: prestadorData.nome,
        description: `Prestador de Serviço - ${prestadorData.empresa} - ${prestadorData.cargo}`,
        price: produtoReferencia.preco,
        quantity: 1,
        category: 'Prestador'
      };

      setLastAddedItem(addedItem);
      setShowCartNotification(true);

      // Esconder notificação após 5 segundos
      setTimeout(() => {
        setShowCartNotification(false);
      }, 5000);

      return addedItem;
    } catch (error) {
      console.error('Erro ao adicionar prestador ao carrinho:', error);
      alert('Erro ao adicionar prestador ao carrinho: ' + error.message);
      return null;
    }
  };

  const navigateToCart = () => {
    navigate('/catalog/new-order');
  };

  // Renderizar notificação de carrinho
  const renderCartNotification = () => {
    if (!showCartNotification) return null;
    
    return (
      <div className="cart-notification-alert">
        <div className="alert">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <FaCheckCircle className="me-2" />
              <strong>Prestador adicionado ao carrinho!</strong>
            </div>
            <Button 
              variant="outline-dark" 
              size="sm"
              onClick={() => setShowCartNotification(false)}
              className="ms-3"
            >
              <FaTimes />
            </Button>
          </div>
          
          {lastAddedItem && (
            <div className="cart-item-preview mt-2 p-2 rounded">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <FaUserTie size={24} className="text-secondary" />
                </div>
                <div>
                  <h6 className="mb-0">{lastAddedItem.name}</h6>
                  <small>{lastAddedItem.description}</small>
                </div>
              </div>
            </div>
          )}
          
          <div className="d-flex justify-content-end mt-3">
            <Button 
              variant="outline-dark" 
              size="sm" 
              className="me-2"
              onClick={() => handleAdd()}
            >
              <FaPlus className="me-1" /> Adicionar Outro
            </Button>
            <Button 
              variant="dark" 
              size="sm"
              onClick={navigateToCart}
            >
              Ir para o Carrinho <FaArrowRight className="ms-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar botão de carrinho
  const renderCartButton = () => {
    if (cartItemsCount === 0) return null;
    
    return (
      <Button 
        variant="outline-dark" 
        className="cart-button ms-2"
        onClick={navigateToCart}
      >
        <FaShoppingCart className="me-1" />
        Carrinho
        <span className="cart-count">{cartItemsCount}</span>
      </Button>
    );
  };

  // Carregar dados essenciais
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isAdmin()) {
          const { data: eventosData } = await supabase.from('caex_eventos').select('*').eq('ativo', true);
          setEventos(eventosData || []);
          const { data: expositoresData } = await supabase.from('caex_expositores').select('id, nome_empresa').eq('ativo', true);
          setExpositores(expositoresData || []);
        } else if (user?.email) {
          const { data: expositorData, error: expError } = await supabase.from('caex_expositores').select('*, caex_expositor_evento(caex_eventos(*))').eq('email', user.email).single();
          if (expError && expError.code !== 'PGRST116') throw expError;
          if (expositorData) {
            setExpositorLogado(expositorData);
            setEventos(expositorData.caex_expositor_evento.map(e => e.caex_eventos).filter(Boolean));
          }
        }
        await fetchPrestadores();
        await fetchProdutos(); // NOVO: Carregar produtos
        await loadCartFromDatabase(); // NOVO: Carregar carrinho
      } catch (err) {
        setError('Erro ao carregar dados iniciais.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // Buscar dados da tabela caex_prestador
  const fetchPrestadores = async () => {
    let query = supabase.from('caex_prestador').select('*, expositor:caex_expositores(nome_empresa)').order('id', { ascending: false });
    
    if (!isAdmin() && user) {
      const { data: expositor } = await supabase.from('caex_expositores').select('id').eq('email', user.email).single();
      if (expositor) {
        query = query.eq('expositor_id', expositor.id);
      }
    }
    
    const { data, error: fetchError } = await query;
    if (fetchError) {
      setError('Falha ao buscar prestadores.');
    } else {
      console.log('Prestadores carregados:', data);
      
      // Aplicar máscara de CPF ao carregar dados
      const dataWithFormattedCpf = data ? data.map(prestador => ({
        ...prestador,
        cpf: prestador.cpf ? formatCpf(prestador.cpf) : ''
      })) : [];
      
      setPrestadores(dataWithFormattedCpf);
    }
  };

  // Handlers
  const handleAdd = () => {
    setEditingPrestador(null);
    setCpfError('');
    setCpfValid(null);
    setFormData({ nome: '', cpf: '', empresa: '', cargo: '', evento_id: '', expositor_id: '' });
    setActiveModalTab('dados'); // NOVO: Resetar para primeira aba
    setShowForm(true);
  };

  const handleEdit = (prestador) => {
    setEditingPrestador(prestador);
    setCpfError('');
    setCpfValid(null);

    const cpfFormatado = prestador.cpf ? formatCpf(prestador.cpf) : '';
    
    setFormData({
      nome: prestador.nome,
      cpf: cpfFormatado,
      empresa: prestador.empresa || '',
      cargo: prestador.cargo || '',
      evento_id: prestador.evento_id?.toString() || '',
      expositor_id: prestador.expositor_id?.toString() || '' // Carrega o expositor para edição
    });

    // Validar CPF se estiver presente
    if (cpfFormatado) {
      const isValid = validateCpf(cpfFormatado);
      setCpfValid(isValid);
    }

    setActiveModalTab('dados'); // NOVO: Resetar para primeira aba
    setShowForm(true);
  };

  const handleDelete = (prestador) => {
    setEditingPrestador(prestador);
    setShowDelete(true);
  };

  const confirmDelete = async (id) => {
    await supabase.from('caex_prestador').delete().eq('id', id);
    setShowDelete(false);
    fetchPrestadores();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { nome, cpf, empresa, cargo, evento_id, expositor_id } = formData;
    if (!nome || !cpf || !empresa || !cargo || !evento_id) {
      alert('Todos os campos são obrigatórios.');
      return;
    }

    // Validar CPF antes de submeter
    if (!validateCpf(cpf)) {
      alert('Por favor, digite um CPF válido.');
      setCpfError('CPF inválido. Verifique os números digitados.');
      return;
    }

    if (isAdmin() && !expositor_id) {
        alert('Administrador deve selecionar um expositor.');
        return;
    }

    const finalExpositorId = isAdmin() ? parseInt(expositor_id) : expositorLogado?.id;

    const prestadorData = {
      nome, 
      cpf: removeCpfMask(cpf), // Remove máscara do CPF antes de salvar
      empresa, 
      cargo,
      evento_id: parseInt(evento_id),
      expositor_id: finalExpositorId,
      tipo_credencial: 'Prestador de Serviço',
      updated_at: new Date()
    };

    try {
      if (editingPrestador) {
        await supabase.from('caex_prestador').update(prestadorData).eq('id', editingPrestador.id);
      } else {
        const { data, error } = await supabase.from('caex_prestador').insert([{ ...prestadorData, created_at: new Date() }]).select();
        if (error) throw error;
        
        // Adicionar ao carrinho no banco de dados
        if (data && data[0]) {
          await addPrestadorToCartDatabase(data[0]);
        }
      }
      setShowForm(false);
      setCpfError('');
      setCpfValid(null);
      fetchPrestadores();
    } catch (err) {
      alert(`Erro ao salvar: ${err.message}`);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const filteredPrestadores = prestadores.filter(p => {
    const search = (filters.search || '').toLowerCase();
    return p.nome.toLowerCase().includes(search) || p.cpf.includes(search) || p.empresa.toLowerCase().includes(search);
  });

  // Estilos do modal
  const modalStyles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem', backdropFilter: 'blur(5px)' },
    content: { position: 'relative', width: '100%', maxWidth: '650px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.2)', overflow: 'hidden', animationName: 'fadeInUp', animationDuration: '0.4s', animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)', animationFillMode: 'both' },
    header: { background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderBottom: '1px solid rgba(0, 0, 0, 0.05)', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
    closeButton: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: 'white', color: '#6c757d', fontSize: '1rem', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' },
    title: { fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a', margin: '0', display: 'flex', alignItems: 'center', gap: '0.75rem' },
    tabs: { display: 'flex', borderBottom: '1px solid rgba(0, 0, 0, 0.05)', backgroundColor: 'white', flexShrink: 0 },
    tab: { padding: '1rem 1.5rem', fontSize: '0.95rem', fontWeight: '500', color: '#6c757d', cursor: 'pointer', transition: 'all 0.3s ease', position: 'relative' },
    activeTab: { color: '#0d6efd', fontWeight: '600' },
    activeTabIndicator: { position: 'absolute', bottom: '-1px', left: '0', right: '0', height: '2px', backgroundColor: '#0d6efd' },
    body: { padding: '2rem', overflowY: 'auto', flex: '1 1 auto' },
    footer: { display: 'flex', justifyContent: 'flex-end', padding: '1.5rem 2rem', borderTop: '1px solid rgba(0, 0, 0, 0.05)', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', flexShrink: 0 },
    footerButton: { padding: '0.75rem 1.5rem', borderRadius: '50px', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.3s ease', cursor: 'pointer' },
    cancelButton: { border: '1px solid #dee2e6', background: 'white', color: '#495057', marginRight: '0.75rem' },
    submitButton: { border: 'none', background: '#212529', color: 'white', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)' },
    formGroup: { marginBottom: '1.5rem' },
    formLabel: { display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#495057' },
    formInput: { width: '100%', padding: '0.75rem 1rem', border: '1px solid #dee2e6', borderRadius: '10px', fontSize: '0.9rem', color: '#495057', transition: 'all 0.3s ease' },
    requiredField: { color: '#dc3545', marginLeft: '0.25rem' },
    requiredNotice: { fontSize: '0.8rem', color: '#dc3545', marginBottom: '1rem' }
  };

  // Adicionar estilos CSS ao carregar o componente
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        font-size: 0.9rem;
        color: #495057;
        transition: all 0.3s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #212529;
        box-shadow: 0 0 0 3px rgba(33, 37, 41, 0.1);
      }
      
      .form-input.cpf-valid {
        border-color: #28a745;
        box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
      }
      
      .form-input.cpf-invalid {
        border-color: #dc3545;
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
      }
      
      .cpf-validation-message {
        font-size: 0.8rem;
        margin-top: 0.25rem;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }
      
      .cpf-validation-message.valid {
        color: #28a745;
      }
      
      .cpf-validation-message.invalid {
        color: #dc3545;
      }
      
      .modal-tab {
        position: relative;
        padding: 1rem 1.5rem;
        font-weight: 500;
        color: #6c757d;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .modal-tab-active {
        color: #0d6efd;
        font-weight: 600;
      }
      
      .modal-tab-active::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #0d6efd;
      }
      
      .modal-tab:hover:not(.modal-tab-active) {
        color: #495057;
        background-color: rgba(0, 0, 0, 0.02);
      }

      .produto-card {
        display: flex;
        align-items: center;
        padding: 0.75rem;
        border: 1px solid #28a745;
        border-radius: 8px;
        margin-bottom: 0.5rem;
        background: #f8fff9;
        cursor: not-allowed;
        transition: all 0.2s ease;
      }
      
      .produto-card.selected {
        border-color: #28a745;
        background-color: #f8fff9;
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
      }
      
      .produto-image {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        background-color: #f8f9fa;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.75rem;
        flex-shrink: 0;
      }
      
      .produto-info {
        flex: 1;
      }
      
      .produto-nome {
        font-weight: 600;
        color: #212529;
        font-size: 0.9rem;
        margin-bottom: 0.25rem;
      }
      
      .produto-preco {
        font-size: 0.8rem;
        color: #28a745;
        font-weight: 500;
      }

      .cart-notification-alert {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 1050;
        width: 350px;
        animation: slideInRight 0.3s ease-out forwards;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        border-radius: 10px;
        overflow: hidden;
      }
      
      .cart-notification-alert .alert {
        margin-bottom: 0;
        padding: 15px 20px;
        border: none;
        border-radius: 10px;
        background: #212529;
        color: white;
      }
      
      .cart-notification-alert .btn-outline-dark {
        color: white;
        border-color: rgba(255, 255, 255, 0.5);
        transition: all 0.2s ease;
      }
      
      .cart-notification-alert .btn-outline-dark:hover {
        background-color: rgba(255, 255, 255, 0.2);
        border-color: white;
      }
      
      .cart-notification-alert .btn-dark {
        background-color: white;
        color: #212529;
        border: none;
        transition: all 0.2s ease;
      }
      
      .cart-notification-alert .btn-dark:hover {
        background-color: #f8f9fa;
        transform: translateY(-2px);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      }
      
      .cart-button {
        position: relative;
        transition: all 0.3s ease;
      }
      
      .cart-button:hover {
        transform: translateY(-2px);
      }
      
      .cart-button .cart-count {
        position: absolute;
        top: -8px;
        right: -8px;
        background-color: #dc3545;
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        font-size: 0.7rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
      }
      
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      .cart-item-added {
        animation: cartBounce 0.5s ease;
      }
      
      @keyframes cartBounce {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      
      .cart-item-preview {
        border: 1px solid rgba(255, 255, 255, 0.3);
        background-color: #f8f9fa;
        color: #212529;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) return <div>Carregando...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        <div className="dashboard-header">
          <h1 className="dashboard-main-title"><FaTools className="dashboard-title-icon" /> Credencial de Prestador</h1>
          <p className="dashboard-subtitle">Gerenciamento de credenciais para prestadores de serviço.</p>
        </div>

        <div className="d-flex justify-content-end mb-4">
          {renderCartButton()}
          
          <Button variant="primary" onClick={handleAdd} className="bg-black text-white px-4 py-2 rounded-md">
            <FaPlus className="me-2" /> Nova Credencial
          </Button>
        </div>

        <Card className="modern-table-card">
          <Card.Body className="modern-card-body">
            <Table responsive hover className="modern-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Empresa Prestadora</th>
                  <th>Cargo/Função</th>
                  <th>Expositor Vinculado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrestadores.map(p => (
                  <tr key={p.id}>
                    <td className="font-weight-bold">{p.nome}</td>
                    <td>{p.cpf}</td>
                    <td>{p.empresa}</td>
                    <td>{p.cargo}</td>
                    <td>{p.expositor?.nome_empresa || 'N/A'}</td>
                    <td>
                      <div className="action-buttons">
                        <Button variant="outline-primary" size="sm" onClick={() => handleEdit(p)}><FaEdit /></Button>
                        {/* <Button variant="outline-danger" size="sm" onClick={() => handleDelete(p)}><FaTrash /></Button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Modal de formulário com abas */}
        {showForm && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  {editingPrestador ? <FaEdit style={{color: '#6c757d'}} /> : <FaPlus style={{color: '#6c757d'}} />}
                  {editingPrestador ? 'Editar' : 'Nova'} Credencial de Prestador
                </h2>
                <button onClick={() => setShowForm(false)} style={modalStyles.closeButton}>✕</button>
              </div>

              {/* NOVO: Sistema de abas */}
              <div style={modalStyles.tabs}>
                <div 
                  className={`modal-tab ${activeModalTab === 'dados' ? 'modal-tab-active' : ''}`}
                  onClick={() => handleTabClick('dados')}
                >
                  Dados Pessoais
                </div>
                <div 
                  className={`modal-tab ${activeModalTab === 'credencial' ? 'modal-tab-active' : ''}`}
                  onClick={() => handleTabClick('credencial')}
                >
                  Credencial & Evento
                </div>
              </div>

              <div style={modalStyles.body} className="modal-scrollable-body">
                {/* ABA DADOS PESSOAIS */}
                {activeModalTab === 'dados' && (
                  <>
                    {/* CAMPO DE VÍNCULO COM EXPOSITOR */}
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.formLabel}>
                        {isAdmin() ? 'Vincular ao Expositor' : 'Vinculado ao Expositor'}
                        {isAdmin() && <span style={modalStyles.requiredField}>*</span>}
                      </label>
                      {isAdmin() ? (
                        <select name="expositor_id" value={formData.expositor_id} onChange={handleChange} style={modalStyles.formInput} required>
                          <option value="">Selecione um expositor...</option>
                          {expositores.map(exp => <option key={exp.id} value={exp.id}>{exp.nome_empresa}</option>)}
                        </select>
                      ) : (
                        <div style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '10px',
                          padding: '0.75rem 1rem',
                          color: '#6c757d',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontWeight: '500'
                        }}>
                          <FaBuilding style={{ color: '#28a745' }} />
                          {expositorLogado?.nome_empresa || 'Não vinculado'}
                        </div>
                      )}
                    </div>

                    <Row>
                      <Col md={6}>
                        <div style={modalStyles.formGroup}>
                          <label style={modalStyles.formLabel}>Nome Completo <span style={modalStyles.requiredField}>*</span></label>
                          <input type="text" name="nome" value={formData.nome} onChange={handleChange} style={modalStyles.formInput} required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div style={modalStyles.formGroup}>
                          <label style={modalStyles.formLabel}>CPF <span style={modalStyles.requiredField}>*</span></label>
                          <input 
                            type="text" 
                            name="cpf" 
                            value={formData.cpf} 
                            onChange={handleCpfChange} 
                            style={{
                              ...modalStyles.formInput,
                              borderColor: cpfValid === true ? '#28a745' : cpfValid === false ? '#dc3545' : '#dee2e6',
                              boxShadow: cpfValid === true ? '0 0 0 3px rgba(40, 167, 69, 0.1)' : 
                                        cpfValid === false ? '0 0 0 3px rgba(220, 53, 69, 0.1)' : 'none'
                            }}
                            placeholder="000.000.000-00"
                            maxLength="14"
                            required 
                          />
                          {cpfError && (
                            <div className="cpf-validation-message invalid">
                              <FaExclamationTriangle />
                              {cpfError}
                            </div>
                          )}
                          {cpfValid === true && (
                            <div className="cpf-validation-message valid">
                              <FaCheckCircle />
                              CPF válido
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <div style={modalStyles.formGroup}>
                                <label style={modalStyles.formLabel}>Empresa Prestadora <span style={modalStyles.requiredField}>*</span></label>
                                <input type="text" name="empresa" value={formData.empresa} onChange={handleChange} style={modalStyles.formInput} required />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div style={modalStyles.formGroup}>
                                <label style={modalStyles.formLabel}>Cargo / Função <span style={modalStyles.requiredField}>*</span></label>
                                <input type="text" name="cargo" value={formData.cargo} onChange={handleChange} style={modalStyles.formInput} placeholder="Ex: Eletricista" required />
                            </div>
                        </Col>
                    </Row>

                    <div style={modalStyles.requiredNotice}>* Campos obrigatórios</div>
                  </>
                )}

                {/* ABA CREDENCIAL & EVENTO */}
                {activeModalTab === 'credencial' && (
                  <>
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.formLabel}>Evento <span style={modalStyles.requiredField}>*</span></label>
                      <select name="evento_id" value={formData.evento_id} onChange={handleChange} style={modalStyles.formInput} required>
                        <option value="">Selecione o evento...</option>
                        {eventos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                      </select>
                    </div>

                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.formLabel}>
                        <FaBox style={{ marginRight: '0.5rem' }} />
                        Produtos Vinculados a Prestadores
                      </label>
                      {renderProdutosDropdown()}
                      <small style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        Produtos automaticamente vinculados a credenciais de prestador (apenas para visualização)
                      </small>
                    </div>

                    <div style={{
                      backgroundColor: '#cff4fc',
                      color: '#055160',
                      padding: '1rem',
                      borderRadius: '10px',
                      border: '1px solid #b6effb',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      <FaExclamationTriangle style={{marginTop: '0.2rem'}} />
                      <div>
                        <strong>Informações sobre Prestadores:</strong><br />
                        <small>
                          • Prestadores têm acesso aos produtos e serviços específicos vinculados ao seu perfil<br />
                          • O acesso é limitado às áreas e horários definidos pelo expositor<br />
                          • Credenciais de prestador incluem identificação da empresa e função específica
                        </small>
                      </div>
                    </div>

                    <div style={modalStyles.requiredNotice}>* Campos obrigatórios</div>
                  </>
                )}
              </div>

              <div style={modalStyles.footer}>
                <button onClick={() => setShowForm(false)} style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}>Cancelar</button>
                <button onClick={handleSubmit} style={{...modalStyles.footerButton, ...modalStyles.submitButton}}>{editingPrestador ? 'Atualizar' : 'Salvar'}</button>
              </div>
            </div>
          </div>
        )}
        
        {showDelete && (
            <div style={modalStyles.overlay}>
                <div style={{...modalStyles.content, maxWidth: '500px'}}>
                    <div style={modalStyles.header}>
                        <h2 style={modalStyles.title}><FaTrash style={{color: '#dc3545'}} /> Confirmar Exclusão</h2>
                        <button onClick={() => setShowDelete(false)} style={modalStyles.closeButton}>✕</button>
                    </div>
                    <div style={modalStyles.body}>
                        <p>Tem certeza que deseja excluir a credencial de <strong>{editingPrestador?.nome}</strong>?</p>
                    </div>
                    <div style={modalStyles.footer}>
                        <button onClick={() => setShowDelete(false)} style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}>Cancelar</button>
                        <button onClick={() => confirmDelete(editingPrestador.id)} style={{...modalStyles.footerButton, background: '#dc3545', color: 'white'}}>Excluir</button>
                    </div>
                </div>
            </div>
        )}

        {/* Notificação de adição ao carrinho */}
        {renderCartNotification()}

      </div>
    </div>
  );
};

export default CredencialPrestador;